import { inject, Injectable, signal } from '@angular/core';
import {
  AuthenticatedResult,
  EventTypes,
  LoginResponse,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { Observable, catchError, EMPTY, of, map, tap, filter, switchMap, iif } from 'rxjs';
import { UserData } from '@shared/interfaces/user-data.interface';
import { environment } from '@environments/environment';
import { WINDOW } from '@shared/consts/browser-providers.const';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly publicEventService = inject(PublicEventsService);
  private readonly window = inject(WINDOW);

  private _userId = '';
  private loginResponse: LoginResponse | null = null;
  accessToken = signal<string | undefined>(undefined);

  get userId(): string {
    return this._userId;
  }

  get isAuthenticated$(): Observable<AuthenticatedResult> {
    return this.oidcSecurityService.isAuthenticated$;
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): Observable<unknown> {
    return this.oidcSecurityService.logoff().pipe(
      tap(() => sessionStorage.removeItem('user-organization')),
      catchError(error => {
        console.error('Error logging out:', error);
        this.window.location.reload();
        return EMPTY;
      })
    );
  }

  checkAuth() {
    return iif(
      () => this.loginResponse == null,
      this.oidcSecurityService.checkAuth(),
      of(this.loginResponse as LoginResponse)
    ).pipe(
      map(loginResponse => {
        const oidcUserData = loginResponse.userData as UserData;
        this._userId = oidcUserData.sub;
        this.accessToken.set(loginResponse.accessToken);
        return { ...loginResponse, userData: oidcUserData };
      }),
      tap(({ isAuthenticated }) => this.alertUserAuthenticated(isAuthenticated)),
      tap(loginResponse => loginResponse.isAuthenticated && (this.loginResponse = loginResponse))
    );
  }

  refreshToken(): Observable<string> {
    return this.publicEventService.registerForEvents().pipe(
      filter(notification => notification.type === EventTypes.NewAuthenticationResult),
      switchMap(() => this.oidcSecurityService.getAccessToken()),
      tap(token => this.accessToken.set(token))
    );
  }

  private alertUserAuthenticated(isAuthenticated: boolean) {
    if (environment.env === 'prod' || this.loginResponse) {
      return;
    }
    const getAlertFormat = (color: 'green' | 'red') =>
      `background-color: ${color}; color: white; font-weight: bold; padding: 8px;`;
    if (!isAuthenticated) {
      console.log('%cIS NOT AUTH', getAlertFormat('red'));
      return;
    }
    console.log('%cIS AUTH', getAlertFormat('green'));
  }
}
